import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import PageFaqSection, { usableFaqs } from './PageFaqSection';
import { PAGE_REGISTRY, pageStorageKey } from '../../data/pageRegistry';

/* ═══════════════════════════════════════════════════════════════════════════
   ROUTE FAQ SECTION
   ─────────────────
   Renders the current page's admin-configured FAQs, mounted ONCE in App.js
   just above the Footer. That single mount point is why every page supports
   FAQs without touching 20 page components — add questions in Pages
   Management → <page> → Content and they appear on that page only.

   Renders nothing unless the current route maps to a registry page AND that
   page has at least one complete question/answer pair.

   Deliberately skipped:
     • Pages that render their own FAQ list (the FAQ page, Support) — a second
       identical block would duplicate both the content and the schema.
     • Item detail pages (partner hotels) — those carry their own per-item
       FAQs, rendered inside the page where they belong contextually.
     • Admin / checkout funnels — no marketing content there.
   ═══════════════════════════════════════════════════════════════════════════ */

// Routes that must never get the shared block.
const SKIP_EXACT = new Set(['/faq', '/support']);
const SKIP_PREFIXES = [
    '/private', '/onboarding', '/confirm-booking', '/payment', '/b2b',
    '/hotels-partner/',   // per-hotel FAQs render inside the detail page
    '/blog/',             // per-post content lives in the article body
];

// path → registry id. Exact match only: a detail route should never inherit
// its listing page's FAQs.
const PATH_TO_ID = PAGE_REGISTRY.reduce((acc, p) => {
    if (p.path) acc[p.path] = p.id;
    return acc;
}, {});

const readFaqs = (pageId) => {
    if (!pageId) return [];
    try {
        const raw = localStorage.getItem(pageStorageKey(pageId));
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed?.faqs) ? parsed.faqs : [];
    } catch {
        return [];
    }
};

const RouteFaqSection = () => {
    const { pathname } = useLocation();
    const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

    const skip = SKIP_EXACT.has(path) || SKIP_PREFIXES.some((p) => path.startsWith(p));
    const pageId = skip ? null : PATH_TO_ID[path] || null;

    const [faqs, setFaqs] = useState(() => readFaqs(pageId));

    // Re-read when the route changes and when settings sync in (admin save or
    // the initial /api/site-settings fetch completing after first paint).
    useEffect(() => { setFaqs(readFaqs(pageId)); }, [pageId]);
    useEffect(() => {
        const sync = () => setFaqs(readFaqs(pageId));
        window.addEventListener('hm-settings-loaded', sync);
        window.addEventListener('storage', sync);
        return () => {
            window.removeEventListener('hm-settings-loaded', sync);
            window.removeEventListener('storage', sync);
        };
    }, [pageId]);

    const items = useMemo(() => usableFaqs(faqs), [faqs]);
    if (!pageId || items.length === 0) return null;

    return <PageFaqSection faqs={items} />;
};

export default RouteFaqSection;
